import { useProfileLinkProps } from "@circle-react/hooks/profile/useProfileLinkProps";
import type { CommunityMember } from "@circle-react/types";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Box } from "@circleco/compass/components/Box";
import { Help } from "@circleco/compass/components/Help";
import { Icon } from "@circleco/compass/components/Icon";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Typography } from "@circleco/compass/components/Typography";
import { useIsTruncated } from "@circleco/compass/hooks/useIsTruncated";
import { useRef } from "react";

import { ConnectionButtonsOrchestrator } from "@/react/components/ConnectionButtons/ConnectionButtonsOrchestrator";
import { useIsMemberDirectoryMapAvailable } from "@/react/components/MemberDirectory/useIsMemberDirectoryMapAvailable";

import { MemberGradientBackground } from "./MemberGradientBackground";
import { useIsBioVisibleInProfileView } from "./useIsBioVisibleInProfileView";

export interface MemberCardWithConnectionProps {
  canReceiveDmFromCurrentMember?: boolean;
  member: CommunityMember;
}

export const MemberCardWithConnection = ({
  member,
  canReceiveDmFromCurrentMember,
}: MemberCardWithConnectionProps) => {
  const profileLinkProps = useProfileLinkProps({
    public_uid: member.public_uid ?? "",
  });

  const isBioVisibleInProfileView = useIsBioVisibleInProfileView();
  const { isAvailable: isLocationVisible } = useIsMemberDirectoryMapAvailable();
  const nameRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLElement>(null);
  const isNameTruncated = useIsTruncated(nameRef);
  const isHeadlineTruncated = useIsTruncated(headlineRef);

  return (
    <Box
      className="flex flex-col h-[274px] justify-between group relative p-4"
      data-testid="community_member_grid"
    >
      <MemberGradientBackground member={member} className="inset-2" />

      {member.bio && isBioVisibleInProfileView && (
        <Help content={member.bio} className="absolute right-4 top-4" />
      )}
      <div className="flex size-full flex-col items-center justify-start gap-4 pt-4">
        <Avatar
          name={member.name ?? ""}
          src={member.avatar_url ?? ""}
          size="2xl"
          aria-label={member.name ?? ""}
          renderLink={<a {...profileLinkProps} />}
        />
        <div className="flex w-full flex-col items-start justify-start gap-2.5 px-2">
          <div className="flex w-full flex-col items-center justify-start gap-0.5">
            <Tooltip
              content={member.name}
              delay={0}
              disabled={!isNameTruncated}
            >
              <a
                {...profileLinkProps}
                tabIndex={-1}
                className="block w-full min-w-0 text-center"
              >
                <Typography ref={nameRef} variant="heading-sm" truncate>
                  {member.name}
                </Typography>
              </a>
            </Tooltip>

            {member.headline && (
              <Tooltip
                content={member.headline}
                delay={0}
                disabled={!isHeadlineTruncated}
              >
                <a
                  {...profileLinkProps}
                  tabIndex={-1}
                  className="block w-full min-w-0 text-center"
                >
                  <Typography
                    ref={headlineRef}
                    variant="caption"
                    color="secondary"
                    truncate
                    className="block w-full"
                  >
                    {member.headline}
                  </Typography>
                </a>
              </Tooltip>
            )}
          </div>
          {member.location && isLocationVisible && (
            <Tooltip
              content={member.location}
              delay={0}
              disabled={member.location.split(",")[0] === member.location}
            >
              <a
                {...profileLinkProps}
                tabIndex={-1}
                className="flex w-full items-center justify-center gap-1 px-2"
              >
                <Icon name="map-pin" size="sm" color="secondary" />
                <span className="min-w-0 text-center">
                  <Typography
                    variant="label-xs"
                    color="secondary"
                    truncate
                    className="block w-full"
                  >
                    {member.location.split(",")[0]}
                  </Typography>
                </span>
              </a>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="flex w-full flex-col items-center justify-end gap-2 px-1 h-[82px]">
        <ConnectionButtonsOrchestrator
          member={member}
          className="w-full"
          fullWidth
          canReceiveDmFromCurrentMember={canReceiveDmFromCurrentMember}
        />
      </div>
    </Box>
  );
};
